import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import Icon from '../../../components/AppIcon';
import { format, parseISO } from 'date-fns';

const ComparisonTools = ({ assessments, progressData }) => {
  if (!assessments || assessments?.length < 2) {
    return null;
  }

  // Prepare comparison data for bar chart
  const comparisonData = assessments?.map(assessment => ({
    name: format(parseISO(assessment?.createdAt), 'MMM dd, yyyy'),
    score: assessment?.results?.overallScore || 0,
    id: assessment?.id
  }));

  // Prepare radar chart data for dimension comparison
  const dimensions = [
    'data_infrastructure',
    'analytics_capabilities',
    'governance_maturity',
    'team_skills',
    'strategic_alignment'
  ];

  const radarData = dimensions?.map(dimension => {
    const dimensionLabel = dimension?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
    const dataPoint = { dimension: dimensionLabel };

    assessments?.forEach((assessment, index) => {
      const progressEntry = progressData?.find(
        p => p?.assessmentId === assessment?.id && p?.dimension === dimension
      );
      dataPoint[`assessment${index + 1}`] = progressEntry?.score || 0;
    });

    return dataPoint;
  });

  // Calculate improvements
  const improvements = [];
  if (assessments?.length >= 2) {
    const latest = assessments?.[0];
    const previous = assessments?.[1];

    dimensions?.forEach(dimension => {
      const latestProgress = progressData?.find(
        p => p?.assessmentId === latest?.id && p?.dimension === dimension
      );
      const previousProgress = progressData?.find(
        p => p?.assessmentId === previous?.id && p?.dimension === dimension
      );

      if (latestProgress && previousProgress) {
        const improvement = latestProgress?.score - previousProgress?.score;
        const improvementPercentage = ((improvement / previousProgress?.score) * 100)?.toFixed(1);

        improvements?.push({
          dimension: dimension?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' '),
          improvement,
          improvementPercentage,
          latestScore: latestProgress?.score,
          previousScore: previousProgress?.score
        });
      }
    });
  }

  const colors = ['#1FB5A5', '#3B82F6', '#8B5CF6'];

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#E6F0F5] rounded-lg flex items-center justify-center">
          <Icon name="GitCompare" size={20} className="text-[#002B45]" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#002B45]">Side-by-Side Comparison</h3>
          <p className="text-sm text-gray-500">Analyzing {assessments?.length} selected assessments</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Overall Score Comparison */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-[#002B45] mb-4">Overall Score Comparison</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={comparisonData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload?.length) {
                    return (
                      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                        <p className="font-semibold text-[#002B45] mb-1">{payload?.[0]?.payload?.name}</p>
                        <p className="text-sm text-[#1FB5A5]">
                          Score: {payload?.[0]?.value}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="score" fill="#1FB5A5" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Dimension Comparison Radar */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-[#002B45] mb-4">Dimension Comparison</h4>
          <ResponsiveContainer width="100%" height={250}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 10 }} />
              <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              {assessments?.map((assessment, index) => (
                <Radar
                  key={assessment?.id}
                  name={format(parseISO(assessment?.createdAt), 'MMM dd')}
                  dataKey={`assessment${index + 1}`}
                  stroke={colors?.[index]}
                  fill={colors?.[index]}
                  fillOpacity={0.3}
                />
              ))}
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Improvement Highlights */}
      {improvements?.length > 0 && (
        <div className="pt-6 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-[#002B45] mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" size={18} className="text-green-600" />
            Improvement Highlights
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {improvements?.map((item, index) => (
              <div
                key={index}
                className={`rounded-lg p-4 ${
                  item?.improvement > 0
                    ? 'bg-green-50 border border-green-200'
                    : item?.improvement < 0
                    ? 'bg-red-50 border border-red-200' :'bg-gray-50 border border-gray-200'
                }`}
              >
                <p className="text-xs font-semibold text-gray-700 mb-2">{item?.dimension}</p>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Previous:</span>
                  <span className="text-lg font-bold text-gray-700">{item?.previousScore}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Latest:</span>
                  <span className="text-lg font-bold text-[#1FB5A5]">{item?.latestScore}</span>
                </div>
                <div className="flex items-center gap-1 text-sm font-semibold">
                  <Icon
                    name={item?.improvement > 0 ? 'TrendingUp' : item?.improvement < 0 ? 'TrendingDown' : 'Minus'}
                    size={16}
                    className={item?.improvement > 0 ? 'text-green-600' : item?.improvement < 0 ? 'text-red-600' : 'text-gray-600'}
                  />
                  <span className={item?.improvement > 0 ? 'text-green-600' : item?.improvement < 0 ? 'text-red-600' : 'text-gray-600'}>
                    {item?.improvement > 0 ? '+' : ''}{item?.improvement} ({item?.improvementPercentage > 0 ? '+' : ''}{item?.improvementPercentage}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonTools;